/**
 * Time rules: persist UTC timestamps; interpret and display business times in
 * Asia/Bangkok. Implemented with Intl so a zone other than Bangkok (or a
 * future DST rule change) still works.
 */
export const BUSINESS_TZ = 'Asia/Bangkok';

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number; // 0=Sunday
}

const partsFormatterCache = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = partsFormatterCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    });
    partsFormatterCache.set(tz, f);
  }
  return f;
}

const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

export function zonedParts(date: Date, tz: string = BUSINESS_TZ): ZonedParts {
  const out: Record<string, string> = {};
  for (const p of partsFormatter(tz).formatToParts(date)) out[p.type] = p.value;
  return {
    year: Number(out.year),
    month: Number(out.month),
    day: Number(out.day),
    hour: Number(out.hour),
    minute: Number(out.minute),
    second: Number(out.second),
    weekday: WEEKDAYS[out.weekday ?? 'Sun'] ?? 0,
  };
}

/** Offset (ms) of `tz` from UTC at the given instant. */
export function tzOffsetMs(date: Date, tz: string = BUSINESS_TZ): number {
  const p = zonedParts(date, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(date.getTime() / 1000) * 1000;
}

/**
 * Convert a wall-clock local date/time in `tz` to a UTC Date.
 * `date` = "YYYY-MM-DD", `time` = "HH:mm" or "HH:mm:ss".
 */
export function zonedWallTimeToUtc(date: string, time: string, tz: string = BUSINESS_TZ): Date {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const tm = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time);
  if (!dm || !tm) throw new RangeError(`วันที่หรือเวลาไม่ถูกต้อง: ${date} ${time}`);
  const [y, mo, d] = [Number(dm[1]), Number(dm[2]), Number(dm[3])];
  const [h, mi, s] = [Number(tm[1]), Number(tm[2]), Number(tm[3] ?? 0)];
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59 || s > 59) {
    throw new RangeError(`วันที่หรือเวลาไม่ถูกต้อง: ${date} ${time}`);
  }
  const wallAsUtc = Date.UTC(y, mo - 1, d, h, mi, s);
  // Two passes handle offset changes around the target instant.
  let guess = wallAsUtc - tzOffsetMs(new Date(wallAsUtc), tz);
  guess = wallAsUtc - tzOffsetMs(new Date(guess), tz);
  const check = zonedParts(new Date(guess), tz);
  if (check.day !== d || check.hour !== h || check.minute !== mi) {
    throw new RangeError(`เวลานี้ไม่มีอยู่จริงในเขตเวลา ${tz}: ${date} ${time}`);
  }
  return new Date(guess);
}

/** "YYYY-MM-DD" of an instant in the business zone. */
export function zonedDateString(date: Date, tz: string = BUSINESS_TZ): string {
  const p = zonedParts(date, tz);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

export function addDaysToDateString(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10);
}

const TH_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const TH_WEEKDAYS_SHORT = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.'];
export const TH_MONTHS_LONG = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
export const TH_WEEKDAYS_LONG = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];

export interface ThaiFormatOptions {
  tz?: string;
  withTime?: boolean;
  withWeekday?: boolean;
  /** Show the Gregorian year in brackets so the year is never ambiguous. */
  showCE?: boolean;
}

/** "วันเสาร์ที่ 26 กันยายน พ.ศ. 2569" — the way the owner reads a date out loud (v1.34.1). */
export function formatThaiDateLong(date: string | Date, opts: { withWeekday?: boolean; tz?: string } = {}): string {
  const d = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date) ? zonedWallTimeToUtc(date, '12:00') : date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const p = zonedParts(d, opts.tz ?? BUSINESS_TZ);
  const day = `${p.day} ${TH_MONTHS_LONG[p.month - 1]} พ.ศ. ${p.year + 543}`;
  return opts.withWeekday === false ? day : `วัน${TH_WEEKDAYS_LONG[p.weekday]}ที่ ${day}`;
}

/** "14 ก.ย. 2569 18:00 น." — Buddhist Era year, optional (ค.ศ. 2026). */
export function formatThaiDateTime(date: Date | string | number, opts: ThaiFormatOptions = {}): string {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const p = zonedParts(d, opts.tz ?? BUSINESS_TZ);
  const be = p.year + 543;
  let s = `${p.day} ${TH_MONTHS_SHORT[p.month - 1]} ${be}`;
  if (opts.showCE) s += ` (ค.ศ. ${p.year})`;
  if (opts.withWeekday) s = `${TH_WEEKDAYS_SHORT[p.weekday]} ${s}`;
  if (opts.withTime !== false) {
    s += ` ${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')} น.`;
  }
  return s;
}

export function formatThaiDate(date: string | Date, opts: Omit<ThaiFormatOptions, 'withTime'> = {}): string {
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    // A plain calendar date has no zone; anchor at Bangkok noon.
    return formatThaiDateTime(zonedWallTimeToUtc(date, '12:00'), { ...opts, withTime: false });
  }
  return formatThaiDateTime(date, { ...opts, withTime: false });
}

/** Relative time for inbox rows: "เมื่อสักครู่", "5 นาที", "3 ชม.", else date. */
export function formatRelativeThai(date: Date | string, now: Date = new Date()): string {
  const d = date instanceof Date ? date : new Date(date);
  const diff = Math.max(0, now.getTime() - d.getTime());
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'เมื่อสักครู่';
  if (min < 60) return `${min} นาที`;
  const hr = Math.floor(min / 60);
  if (hr < 24 && zonedDateString(d) === zonedDateString(now)) return `${hr} ชม.`;
  if (hr < 48) return 'เมื่อวาน';
  const p = zonedParts(d);
  const n = zonedParts(now);
  if (p.year === n.year) return `${p.day} ${TH_MONTHS_SHORT[p.month - 1]}`;
  return formatThaiDate(d);
}

export interface QuietHours {
  /** "HH:mm" local start (inclusive) */
  start: string;
  /** "HH:mm" local end (exclusive); may be earlier than start (overnight) */
  end: string;
  tz?: string;
}

function minutesOf(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

export function isWithinQuietHours(at: Date, q: QuietHours | null | undefined): boolean {
  if (!q) return false;
  const p = zonedParts(at, q.tz ?? BUSINESS_TZ);
  const cur = p.hour * 60 + p.minute;
  const s = minutesOf(q.start);
  const e = minutesOf(q.end);
  if (s === e) return false;
  return s < e ? cur >= s && cur < e : cur >= s || cur < e;
}

/** First instant at/after `at` that is outside quiet hours. */
export function nextAllowedTime(at: Date, q: QuietHours | null | undefined): Date {
  if (!isWithinQuietHours(at, q) || !q) return at;
  const tz = q.tz ?? BUSINESS_TZ;
  const today = zonedDateString(at, tz);
  let candidate = zonedWallTimeToUtc(today, q.end, tz);
  if (candidate.getTime() <= at.getTime()) {
    candidate = zonedWallTimeToUtc(addDaysToDateString(today, 1), q.end, tz);
  }
  return candidate;
}
